import { useEffect, useRef, useState } from 'react';
import { Dialog, Box, IconButton, TextField, Button, Typography, CircularProgress } from 'src/components';
import { ArrowBackIcon, CheckIcon } from 'src/components';
import { useIsMobile } from 'src/hooks';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
mapboxgl.accessToken = MAPBOX_TOKEN;

export default function MapPickerModal({ open, onClose, onConfirm, initialCoords }) {
  const isMobile = useIsMobile();
  const mapContainerRef = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);

  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [saving, setSaving] = useState(false); 

  useEffect(() => {
    if (!open) {
      if (map.current) {
        map.current.remove();
        map.current = null;
        marker.current = null;
      }
      return;
    }

    const timer = setTimeout(() => {
      if (!mapContainerRef.current || map.current) return;

      const centerLng = initialCoords?.lng ?? -70;
      const centerLat = initialCoords?.lat ?? 8;
      const zoom = initialCoords ? 12 : 3;

      map.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/outdoors-v12',
        center: [centerLng, centerLat],
        zoom: zoom,
      });

      if (!isMobile) {
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      }

      marker.current = new mapboxgl.Marker({
        color: '#ff0000',
        scale: 1.3,
      })
        .setLngLat([centerLng, centerLat])
        .addTo(map.current);

      setLat(centerLat.toFixed(6));
      setLng(centerLng.toFixed(6));

      map.current.on('move', () => {
        const center = map.current.getCenter();
        setLat(center.lat.toFixed(6));
        setLng(center.lng.toFixed(6));
        marker.current?.setLngLat(center);
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [open, initialCoords, isMobile]);

  const handleLatChange = (e) => {
    const value = e.target.value;
    setLat(value);
    const num = parseFloat(value);
    if (!isNaN(num) && num >= -90 && num <= 90 && map.current) {
      const { lng } = map.current.getCenter();
      map.current.easeTo({ center: [lng, num] });
    }
  };

  const handleLngChange = (e) => {
    const value = e.target.value;
    setLng(value);
    const num = parseFloat(value);
    if (!isNaN(num) && num >= -180 && num <= 180 && map.current) {
      const { lat } = map.current.getCenter();
      map.current.easeTo({ center: [num, lat] });
    }
  };

  const handleConfirm = async () => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    if (isNaN(latitude) || isNaN(longitude)) return;

    setSaving(true);

    let countryCode = null;

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&types=country`
      );
      const data = await response.json();
      if (data.features?.[0]?.properties?.short_code) {
        countryCode = data.features[0].properties.short_code.toUpperCase();
      }
    } catch (err) {
      console.warn('Could not fetch country (offline or limit)', err);
    }

    onConfirm({
      label: "Pinned location",
      coords: { lat: latitude, lng: longitude },
      countryCode
    });
    setSaving(false);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          height: isMobile ? '100%' : '90vh',
          m: 0,
          ...(isMobile && {
            marginTop: 'env(safe-area-inset-top)',
            marginBottom: 'env(safe-area-inset-bottom)',
            height: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
          }),
        },
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <IconButton
          onClick={onClose}
          size="large"
          sx={{
            position: 'absolute',
            top: isMobile ? 'calc(16px + env(safe-area-inset-top))' : 16,
            left: 16,
            zIndex: 1000,
            bgcolor: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <div
          ref={mapContainerRef}
          style={{ width: '100%', height: '100%' }}
        />

        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            bottom: isMobile ? 'max(24px, env(safe-area-inset-bottom))' : 24,
            left: 0,
            right: 0,
            mx: 3,
            bgcolor: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: '0 -8px 32px rgba(0,0,0,0.15)',
            zIndex: 100,
            p: 3,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} textAlign="center" sx={{ mb: 2 }}>
            Drag map or enter coordinates
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Latitude"
              value={lat}
              onChange={handleLatChange}
              size="small"
              sx={{ flex: 1 }}
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField
              label="Longitude"
              value={lng}
              onChange={handleLngChange}
              size="small"
              sx={{ flex: 1 }}
              inputProps={{ inputMode: 'decimal' }}
            />
            <Button
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                handleConfirm();
              }}
              disabled={saving}
              sx={{ height: 40, minWidth: 56, borderRadius: 2 }}
            >
              {saving ? <CircularProgress size={20} /> : <CheckIcon />}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}